import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Briefcase, User } from 'lucide-react';

import { FIREBASE_CONFIG } from '../../config/Config';
import { scanForConflicts } from '../../utils/conflictScanner';
import { searchClientsByKeywords, searchMattersByKeywords } from '../../services/SmartEntityLinkerRepository';

const appId = FIREBASE_CONFIG.projectId;

export const SmartEntityLinker = ({ onSelect, placeholder = 'Buscar cliente ou processo...' }) => {
  const [term, setTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState({ clients: [], matters: [] });
  const [conflictSummary, setConflictSummary] = useState(null);

  const rootRef = useRef(null);
  const seqRef = useRef(0);

  const normalized = useMemo(() => String(term || '').trim().toLowerCase(), [term]);
  const token = useMemo(() => normalized.split(/\s+/).filter(Boolean).slice(-1)[0] || '', [normalized]);
  const digitToken = useMemo(() => token.replace(/\D/g, ''), [token]);

  useEffect(() => {
    const onDown = (e) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    const qTerm = token;
    const qDigits = digitToken;

    if (qTerm.length < 2) {
      setResults({ clients: [], matters: [] });
      setConflictSummary(null);
      setIsSearching(false);
      setIsOpen(false);
      return;
    }

    const seq = ++seqRef.current;
    setIsSearching(true);

    const t = setTimeout(async () => {
      try {
        const keywords = [qTerm];
        if (qDigits && qDigits !== qTerm) keywords.push(qDigits);

        const [clientsDocs, mattersDocs] = await Promise.all([
          searchClientsByKeywords(keywords, appId),
          searchMattersByKeywords(keywords, appId),
        ]);

        if (seq !== seqRef.current) return;

        const clientsMap = new Map();
        clientsDocs.forEach((d) => {
            const data = d.data() || {};
            const name =
              data?.qualification?.physical?.name ||
              data?.qualification?.legal?.companyName ||
              data?.anchor?.name ||
              data?.name ||
              'Cliente';
            const document = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';

            clientsMap.set(String(d.id), {
              id: d.id,
              type: 'client',
              name: String(name || '').trim(),
              document: String(document || '').trim(),
              raw: data,
            });
          });

        const mattersMap = new Map();
        mattersDocs.forEach((d) => {
          const data = d.data() || {};
          const cnjNumber = data?.numeroCNJ || data?.cnjNumber || '';
          const title = data?.title || data?.assunto || data?.clientName || 'Processo';

          mattersMap.set(String(d.id), {
            id: d.id,
            type: 'matter',
            name: String(title || '').trim(),
            cnjNumber: String(cnjNumber || '').trim(),
            raw: data,
          });
        });

        const summary = await scanForConflicts(normalized, qDigits, { appId });
        if (seq !== seqRef.current) return;

        const matchMap = new Map();
        (Array.isArray(summary?.matches) ? summary.matches : []).forEach((item) => {
          const key = `${String(item?.source || '')}:${String(item?.entityId || '')}`;
          matchMap.set(key, item);
        });

        const clients = Array.from(clientsMap.values()).slice(0, 8).map((item) => ({
          ...item,
          conflict: matchMap.get(`clients:${String(item.id || '')}`) || null,
        }));
        const matters = Array.from(mattersMap.values()).slice(0, 8).map((item) => ({
          ...item,
          conflict: matchMap.get(`processes:${String(item.id || '')}`) || null,
        }));

        setResults({ clients, matters });
        setConflictSummary(summary?.hasConflict ? summary : null);
        setIsOpen(true);
      } catch {
        if (seq !== seqRef.current) return;
        setResults({ clients: [], matters: [] });
        setConflictSummary(null);
        setIsOpen(false);
      } finally {
        if (seq === seqRef.current) setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [token, digitToken]);

  const hasAny = results.clients.length > 0 || results.matters.length > 0;

  return (
    <div ref={rootRef} className="relative w-full">
      <div className="relative">
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => {
            if (hasAny) setIsOpen(true);
          }}
          className="w-full pl-10 pr-10 py-2 bg-gray-100 border border-transparent focus:bg-white focus:border-[#C5A059] rounded-xl text-sm outline-none shadow-sm"
          placeholder={placeholder}
          autoComplete="off"
        />

        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {isSearching ? (
            <i className="fas fa-spinner fa-spin text-xs" />
          ) : (
            <i className="fas fa-search text-xs" />
          )}
        </div>

        {term ? (
          <button
            type="button"
            onClick={() => {
              setTerm('');
              setResults({ clients: [], matters: [] });
              setConflictSummary(null);
              setIsOpen(false);
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            aria-label="Limpar busca"
          >
            <i className="fas fa-times text-xs" />
          </button>
        ) : null}
      </div>

      {conflictSummary?.hasConflict ? (
        <div className={`mt-2 rounded-xl border px-3 py-2 text-xs ${conflictSummary.status === 'danger' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <div className="font-extrabold uppercase tracking-widest text-[10px]">
            {conflictSummary.status === 'danger' ? 'Conflito Ético Detectado' : 'Atenção de Compliance'}
          </div>
          <div className="mt-1 font-bold">{conflictSummary.message}</div>
        </div>
      ) : null}

      {isOpen && hasAny ? (
        <div className="absolute top-full left-0 w-full mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl z-50">
          {results.clients.length > 0 ? (
            <div>
              <div className="px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
                Clientes Encontrados
              </div>
              <div className="max-h-64 overflow-auto">
                {results.clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      if (onSelect) onSelect({ id: c.id, type: c.type, name: c.name, raw: c.raw, conflict: c.conflict, conflictSummary });
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-start gap-2"
                  >
                    <span className="mt-0.5 text-blue-700 flex-shrink-0">
                      <User size={16} />
                    </span>
                    <span className="min-w-0">
                      <div className="text-xs font-bold text-slate-800 truncate">{c.name}</div>
                      {c.document ? (
                        <div className="text-[11px] text-slate-500 truncate">{c.document}</div>
                      ) : null}
                      {c.conflict ? (
                        c.conflict.type === 'PARTE_ADVERSA' ? (
                          <div className="mt-1 inline-flex max-w-full items-center gap-1 rounded bg-red-600 text-white text-[10px] font-bold px-2 py-1 shadow-sm uppercase tracking-wider">
                            <span>⚠️</span>
                            <span className="truncate">Parte Adversa</span>
                          </div>
                        ) : (
                          <div className="mt-1 text-[10px] font-extrabold text-amber-700">
                            ⚠️ {c.conflict.message}
                          </div>
                        )
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {results.matters.length > 0 ? (
            <div className={results.clients.length > 0 ? 'border-t border-slate-200' : ''}>
              <div className="px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
                Processos Encontrados
              </div>
              <div className="max-h-64 overflow-auto">
                {results.matters.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      if (onSelect) onSelect({ id: m.id, type: m.type, name: m.name, raw: m.raw, conflict: m.conflict, conflictSummary });
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-start gap-2"
                  >
                    <span className="mt-0.5 text-emerald-700 flex-shrink-0">
                      <Briefcase size={16} />
                    </span>
                    <span className="min-w-0">
                      <div className="text-xs font-bold text-slate-800 truncate">{m.name}</div>
                      {m.cnjNumber ? (
                        <div className="text-[11px] text-slate-500 truncate">{m.cnjNumber}</div>
                      ) : null}
                      {m.conflict ? (
                        m.conflict.type === 'PARTE_ADVERSA' ? (
                          <div className="mt-1 inline-flex max-w-full items-center gap-1 rounded bg-red-600 text-white text-[10px] font-bold px-2 py-1 shadow-sm uppercase tracking-wider">
                            <span>⚠️</span>
                            <span className="truncate">Parte Adversa</span>
                          </div>
                        ) : (
                          <div className="mt-1 text-[10px] font-extrabold text-amber-700">
                            ⚠️ {m.conflict.message}
                          </div>
                        )
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
